import type { Player } from '@uefadigital/panenka';
import { EventType as EventTypePanenka, PlayerEventSubType } from '@uefadigital/panenka';

import type { MatchEvents, LineUpPlayer } from 'types/ApiResponseInterfaces';
import { translateEntity } from 'utils/translateEntity';

export const EventType: Record<string, string> = {
  YELLOW_CARD: EventTypePanenka.YELLOW_CARD,
  RED_YELLOW_CARD: EventTypePanenka.RED_YELLOW_CARD,
  GOAL: EventTypePanenka.GOAL,
  OWN_GOAL: PlayerEventSubType.OWN_GOAL,
  SUBSTITUTION: EventTypePanenka.SUBSTITUTION,
  SUBSTITUTION_BACK: 'SUBSTITUTION_BACK',
  RED_CARD: EventTypePanenka.RED_CARD,
};

export const EventSubType: Record<string, string> = {
  OWN_GOAL: PlayerEventSubType.OWN_GOAL,
};

export const getEventsByTypes = (events: Array<MatchEvents>, types: Array<string>) =>
  events?.filter((ev) => types.includes(EventType[ev.type])) ?? [];

const getPlayerEvents = (events: Array<MatchEvents>, id: string, types: Array<string>) => {
  const eventsArray = getEventsByTypes(events, types);

  return eventsArray.filter((ev) => ev.primaryActor?.person?.id === id);
};

export const getSubstitutionEvents = (events: Array<MatchEvents>, id: string, isBench: boolean) =>
  getEventsByTypes(events, [EventType.SUBSTITUTION])
    .filter((ev) => ev.primaryActor?.person?.id === id || (isBench && ev.secondaryActor?.person?.id === id))
    .map((ev) => ({
      ...ev,
      type: ev.primaryActor.person.id === id ? 'out' : 'in',
    }));

export const getPlayerGoals = (player: Player, events: Array<MatchEvents>) => {
  const goals = getPlayerEvents(events, player.id, [EventType.GOAL]);

  const ownGoalsNumber = goals.filter((g) => g.subType === PlayerEventSubType.OWN_GOAL).length;

  const goalsNumber = goals.length - ownGoalsNumber;

  return { goalsNumber, ownGoalsNumber };
};

export const getPlayerCards = (player: Player, events: Array<MatchEvents>) => {
  const redYellowCards = getPlayerEvents(events, player.id, [EventType.RED_YELLOW_CARD]).length;

  if (redYellowCards > 0) return 'yellow-red';

  const redCards = getPlayerEvents(events, player.id, [EventType.RED_CARD]).length;

  if (redCards > 0) return 'red';

  const yellowCards = getPlayerEvents(events, player.id, [EventType.YELLOW_CARD]).length;

  if (yellowCards > 0) return 'yellow';
};

export const getPlayerSubstitutions = (player: Player, events: Array<MatchEvents>, isBench: boolean) => {
  const substitutions = getSubstitutionEvents(events, player.id, isBench);

  if (substitutions.length === 0) return;

  return substitutions[0].type as 'out' | 'in' | undefined;
};

export const playerPosition = ({ fieldCoordinate }: LineUpPlayer, isHome: boolean) => {
  if (!fieldCoordinate) return null;
  const x = fieldCoordinate.x / 10;
  const y = fieldCoordinate.y / 10;

  const homeTop = `${y * 0.5 + 1}%`;
  const homeLeft = `${x}%`;
  const awayTop = `${(192 - y) / 1.93}%`;
  const awayLeft = `${101 - x}%`;

  if (fieldCoordinate) {
    if (isHome) {
      return {
        top: homeTop,
        left: homeLeft,
      };
    }

    return {
      top: awayTop,
      left: awayLeft,
    };
  }
};

export const displayPlayerName = (player: LineUpPlayer) => {
  if (translateEntity(player?.player)?.shortName) {
    return translateEntity(player.player).shortName;
  }

  return `Player ${player.jerseyNumber}`;
};
